/**
 * Contacts, and the calling list they form (S05).
 *
 * The list is one endpoint with a tab, because "Waiting to call" and "Call
 * history" are not two collections — they are one collection split by whether a
 * contact's last call resolved. S06 puts it plainly: "contacts move automatically
 * as calls resolve." Two endpoints would let the two views disagree about a
 * contact that resolved between requests.
 */
import { z } from 'zod';

import { CallDisposition, CallOutcome, Salutation } from './enums.js';

/**
 * E.164, which is what the dialler needs and therefore what we store.
 *
 * Deliberately strict: a number that cannot be dialled is worse than a rejected
 * import row, because it looks like a contact and fails at call time. Import
 * normalises before validating, so an operator's `0888 123 456` is not rejected
 * for its formatting — see `csvContactRowSchema`.
 */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{7,14}$/, 'must be an E.164 number, for example +359888123456');

/**
 * One address, validated the same way everywhere it is accepted.
 *
 * Shared so the import and the add-a-contact form cannot disagree about what an
 * address is: the import needs to check one without building a whole row, since a
 * bad address is dropped rather than rejecting the contact.
 */
export const emailSchema = z.string().trim().email().max(200);

/** Nullable rather than optional: the table renders "—" for a missing address. */
export const contactSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  company: z.string().nullable(),
  phone: z.string(),
  email: z.string().nullable(),
  salutation: Salutation.schema,
  doNotCall: z.boolean(),
  createdAt: z.string().datetime(),
  /**
   * The most recent call's outcome, or null when never called.
   *
   * This is what the tabs split on and what the row's status chip shows, so it
   * is denormalised into the list response rather than left to the client to
   * derive from a calls array it does not have.
   */
  lastOutcome: CallOutcome.schema.nullable(),
  /**
   * How the platform says the last call ended, when it said.
   *
   * The detail an outcome alone hides: a MISSED call nobody picked up and one
   * the contact turned down are the same outcome and different news. Null while
   * the call is still in flight, and for calls resolved before the platform
   * started reporting it.
   */
  lastDisposition: CallDisposition.schema.nullable(),
  /**
   * The last call is DIALING and the contact has picked up.
   *
   * Separate from the outcome on purpose: the call is not over, so it is not
   * ANSWERED yet, and the concurrency gate counts DIALING rows as live calls
   * either way. This is what turns "Calling…" into "Call in progress".
   */
  inCall: z.boolean(),
  /**
   * Why the last call failed, when it did.
   *
   * Carried beside the outcome because "Failed" on its own is the least useful
   * thing this row can say: an operator reading it against a phone number will
   * reasonably conclude the number is bad, when the truth is usually that we
   * never heard back. Null for every other outcome, since there is nothing to
   * explain.
   */
  lastFailureReason: z.string().nullable(),
  lastCalledAt: z.string().datetime().nullable(),
  /** Queued already, so the UI can disable "Add to queue" rather than fail it. */
  inQueue: z.boolean(),
  /** Next scheduled call, if any — shown in the row and used by the dialog. */
  scheduledFor: z.string().datetime().nullable(),
});
export type Contact = z.infer<typeof contactSchema>;

/**
 * Which half of the list.
 *
 * `waiting` is "no call has resolved yet", which includes never-called contacts
 * and ones whose call is still PENDING or DIALING. `history` is the complement.
 * Defined here rather than in the query so both sides share one definition of
 * the split.
 */
export const CONTACT_TAB = ['waiting', 'history'] as const;
export const contactTabSchema = z.enum(CONTACT_TAB);
export type ContactTab = z.infer<typeof contactTabSchema>;

/** Outcomes that mean a call is finished, and the contact belongs in history. */
export const RESOLVED_OUTCOMES = [
  'ANSWERED',
  'MISSED',
  'FAILED',
] as const satisfies readonly CallOutcome[];

export const contactListQuerySchema = z.object({
  tab: contactTabSchema.default('waiting'),
  /** Matched against name, company, phone and email (S05). */
  search: z.string().trim().max(120).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export type ContactListQuery = z.infer<typeof contactListQuerySchema>;

/**
 * Both tab counts, returned alongside the page.
 *
 * S06 wants a live count on each tab, and the counts have to come from the same
 * transaction as the rows — otherwise the tab you are not looking at can be
 * wrong, which is exactly the case nobody notices until a demo.
 */
export const contactListSchema = z.object({
  items: z.array(contactSchema),
  total: z.number().int().min(0),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  counts: z.object({
    waiting: z.number().int().min(0),
    history: z.number().int().min(0),
  }),
});
export type ContactList = z.infer<typeof contactListSchema>;

export const createContactSchema = z.object({
  name: z.string().trim().min(1).max(160),
  company: z.string().trim().max(160).optional(),
  phone: phoneSchema,
  email: emailSchema.optional(),
  salutation: Salutation.schema.default('NONE'),
});
export type CreateContact = z.infer<typeof createContactSchema>;

/**
 * Every field optional, but not `doNotCall`.
 *
 * Suppression is permanent and survives re-import (Q14), so it is not something
 * a general-purpose edit may toggle off by omission or otherwise. It gets its own
 * endpoint, where the intent is unambiguous.
 */
export const updateContactSchema = createContactSchema.partial();
export type UpdateContact = z.infer<typeof updateContactSchema>;

/**
 * The inline salutation control (S05): "saved immediately".
 *
 * Its own endpoint rather than a general update, because it is edited from the
 * table on every row and a whole-contact PATCH from a select element invites
 * clobbering a field somebody else just changed.
 */
export const updateSalutationSchema = z.object({
  salutation: Salutation.schema,
});
export type UpdateSalutation = z.infer<typeof updateSalutationSchema>;
